/**
 * What this machine says about itself, while something is asking (RFC 0276 §3).
 *
 * ITS OWN MODULE, beside the transport rather than inside the panel, because
 * two different questions want it: the panel asks "which shells are on this
 * machine", and the shell asks "is there a machine here at all" in order to
 * decide whether the new-pane page offers to make one. Neither of those is a
 * fact about the terminal VIEW, so neither should have to reach through it.
 *
 * A POLL rather than a subscription, because the source is a plain HTTP probe
 * on localhost and the list is small. Shells appear and exit on their own — an
 * `exit`, the idle reaper — so a list that refreshed only on mount would offer
 * a shell that is not there. Five seconds is well under the time it takes to
 * wonder whether the list is stale.
 */

import { useEffect, useState } from 'react'
import { type LocalTerminals, probeLocalTerminals } from './termClient'

export function useLocalTerminals(
  active: boolean,
  /** Whose checkout the shells are in (RFC 0331 3a). */
  projectId: string,
): {
  probe: LocalTerminals | null
  /** True only until the FIRST answer. A later failure leaves the last known
   *  answer in place rather than flipping the caller back to "looking…". */
  loading: boolean
  refresh: () => void
} {
  const [probe, setProbe] = useState<LocalTerminals | null>(null)
  const [loading, setLoading] = useState(true)
  const [nonce, setNonce] = useState(0)

  /**
   * `nonce` is not read by the effect — it IS the refresh signal. Bumping it
   * restarts the poll, which is how a caller says "ask again NOW" (a shell was
   * just started, or just ended) rather than waiting out the interval.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: nonce is the refresh signal, not a value the effect reads
  useEffect(() => {
    if (!active) return
    let live = true
    const controller = new AbortController()
    const tick = async () => {
      const answer = await probeLocalTerminals(projectId, controller.signal)
      if (!live) return
      setProbe(answer)
      setLoading(false)
    }
    void tick()
    const timer = setInterval(() => void tick(), 5000)
    return () => {
      live = false
      controller.abort()
      clearInterval(timer)
    }
  }, [active, nonce, projectId])

  return { probe, loading, refresh: () => setNonce((n) => n + 1) }
}
